import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';
import { useGetLoanApplicationByTask, useGetVerifications, useUpdateVerificationCategory, useUpdateVerificationItem, useUploadDocument, useGetDocumentsByLoanApplication, cancelLoanApplication } from '../../../services/loanService';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import { downloadDocument } from '../../../services/documentService';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas';
import { UseQueryOptions } from '@tanstack/react-query';
import { VerificationStatus } from '../../../services/schemas/loanServiceSchemas/verificationStatus';
import { VerificationUpdate } from '../../../services/schemas/loanServiceSchemas/verificationUpdate';
import { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import { VerificationItem as ApiVerificationItem } from '../../../services/schemas/loanServiceSchemas/verificationItem';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { formatDateTime } from '../../../utils/formatters';
import DocumentValidityBadge from '../../common/DocumentValidityBadge';
import DocumentActionsMenu from '../../common/DocumentActionsMenu';
import { DocumentPreviewModal, useDocumentPreview } from '../../common/DocumentPreview';
import { getDocumentUploadDate } from '../../../utils/documentDates';
import { CHECK_DOCUMENT_TYPES, getCheckCode, getCheckDocuments } from '../../../constants/verificationChecks';
import { DECLINE_REASON_LABELS } from '../../../constants/declineReasons';

// Define the verification category type from API
interface VerificationCategoryType {
    categoryCode?: string;
    title?: string;
    items?: ApiVerificationItem[];
    verifiedCount?: number;
    totalCount?: number;
}

// Define the order of items for each category
const ITEM_ORDER: Record<string, string[]> = {
    'bank': ['ACCOUNT_STATUS', 'ACCOUNT_HOLDER', 'BANK_STATEMENTS'],
    'identity': ['ID_DOC', 'PHOTO_MATCH'],
    'business': ['PURCHASE_ORDER', 'AWARD_LETTER'],
    'collateral': ['ASSET_EXISTENCE', 'ASSET_VALUE', 'OWNERSHIP_DOCS'],
    'company': ['REGISTRATION', 'TAX_COMPLIANCE', 'BUSINESS_ADDRESS'],
    // Affordability is no longer a verification check (finding #15); older applications that
    // already recorded it still show it, after these.
    'screening': ['CREDIT_CHECK', 'CRIMINAL_RECORD']
};

interface VerificationItem {
    id: string;
    label: string;
    status: 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NOT_PROVIDED';
    required: boolean;
    requiresUpload?: boolean;
    notes?: string;
    verifiedByName?: string;
    verifiedAt?: string;
}

interface VerificationSection {
    id: string;
    title: string;
    icon: string;
    items: VerificationItem[];
}

const VerificationDetails = () => {
    // The taskId is passed as a URL parameter, not a query parameter
    const { applicationId } = useParams<{ applicationId: string }>();
    const location = useLocation();
    const [activeSection, setActiveSection] = useState<string>('identity');
    const [sections, setSections] = useState<VerificationSection[]>([
        {
            id: 'identity',
            title: 'Directors Identity Verification',
            icon: 'fa-id-card',
            items: [
                { id: 'ID_DOC', label: 'ID Document', status: 'PENDING', required: true }
            ]
        },
        {
            id: 'bank',
            title: 'Bank Account Verification',
            icon: 'fa-university',
            items: [
                { id: 'BANK_STATEMENTS', label: 'Bank Statements', status: 'PENDING', required: true },
                { id: 'ACCOUNT_HOLDER', label: 'Account Holder Verification', status: 'PENDING', required: true },
                { id: 'ACCOUNT_STATUS', label: 'Account Status', status: 'PENDING', required: true },
            ]
        },
        {
            id: 'business',
            title: 'Proof of Income',
            icon: 'fa-file-contract',
            items: [
                { id: 'PURCHASE_ORDER', label: 'Income Information', status: 'PENDING', required: true },
                { id: 'AWARD_LETTER', label: 'Letter of Award', status: 'PENDING', required: true },
            ]
        },
        {
            id: 'collateral',
            title: 'Collateral Verification',
            icon: 'fa-shield-alt',
            items: [
                { id: 'ASSET_EXISTENCE', label: 'Asset Existence', status: 'PENDING', required: true },
                { id: 'ASSET_VALUE', label: 'Asset Valuation', status: 'PENDING', required: true },
                { id: 'OWNERSHIP_DOCS', label: 'Ownership Documents', status: 'PENDING', required: true },
            ]
        },
        {
            id: 'company',
            title: 'Company Verification',
            icon: 'fa-building',
            items: [
                { id: 'REGISTRATION', label: 'Company Registration', status: 'PENDING', required: true },
                { id: 'TAX_COMPLIANCE', label: 'Tax Compliance', status: 'PENDING', required: true },
                { id: 'BUSINESS_ADDRESS', label: 'Business Address', status: 'PENDING', required: true },
            ]
        },
        {
            id: 'screening',
            title: 'Pre-Screening Checks',
            icon: 'fa-search',
            items: [
                { id: 'CREDIT_CHECK', label: 'Director Credit Checks', status: 'PENDING', required: false },
                { id: 'CRIMINAL_RECORD', label: 'Criminal Record Check', status: 'PENDING', required: false },
            ]
        }
    ]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRejected, setIsRejected] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(new Set());
    const [showRejectForm, setShowRejectForm] = useState(false);
    const documentPreview = useDocumentPreview(message => toast.error(message));
    const [showSubmitWithRejectionsModal, setShowSubmitWithRejectionsModal] = useState(false);
    const [rejectionComment, setRejectionComment] = useState('');
    // Reportable reason category for the rejection
    const [rejectionReason, setRejectionReason] = useState('');
    // A rejected check goes to credit, which needs to know why -- so rejecting one asks for a reason.
    const [itemToReject, setItemToReject] = useState<{ sectionId: string; item: VerificationItem } | null>(null);
    const [itemRejectionReason, setItemRejectionReason] = useState('');
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);
    const navigate = useNavigate();
    // Recorded against each decision as who made it, instead of the login id.
    const user = useSelector((state: RootState) => state.auth.user);
    const recordedBy = user?.name || user?.username;

    // We'll use the applicationId from the URL as the taskId initially
    const taskId = applicationId;

    // State to store the actual loan application ID once we get it from the API
    const [loanApplicationId, setLoanApplicationId] = useState<string | undefined>();

    console.log('Initial params:', { taskId, applicationId });

    // First, fetch loan application by task ID
    const {
        data: loanApplication,
        isLoading: isLoadingApplication,
        error: applicationError
    } = useGetLoanApplicationByTask(
        taskId || '',
        {
            query: {
                enabled: !!taskId, // Enable this query if we have a taskId
                onSuccess: (data: LoanApplication) => {
                    console.log('Loan application fetched by task:', data);
                    if (data?.id) {
                        console.log('Setting loan application ID:', data.id);
                        setLoanApplicationId(data.id);
                    } else {
                        console.error('Loan application data does not contain an ID');
                    }
                },
                onError: (error: Error) => {
                    console.error('Error fetching loan application:', error);
                    toast.error(`Failed to load application: ${error.message}`);
                }
            } as Partial<UseQueryOptions<LoanApplication, Error>>
        }
    );

    // Make sure loanApplicationId is set when loanApplication data is available
    useEffect(() => {
        if (loanApplication && loanApplication.id && !loanApplicationId) {
            console.log('Setting loan application ID from effect:', loanApplication.id);
            setLoanApplicationId(loanApplication.id);
        }
    }, [loanApplication, loanApplicationId]);

    // Then, fetch verifications using the loan application ID
    const {
        data: verificationCategories,
        isLoading: isLoadingVerifications,
        error: verificationsError,
        refetch: refetchVerifications
    } = useGetVerifications(
        loanApplicationId || '',
        {
            query: {
                enabled: !!loanApplicationId // Only fetch if we have a valid application ID
            }
        }
    );

    // Fetch documents for the loan application to check for existing uploads
    const {
        data: documents,
        isLoading: isLoadingDocuments,
        refetch: refetchDocuments
    } = useGetDocumentsByLoanApplication(
        loanApplicationId || '',
        undefined,
        {
            query: {
                enabled: !!loanApplicationId
            }
        }
    );

    // Set active section when sections are loaded or updated
    useEffect(() => {
        if (sections.length > 0) {
            // Only set active section if it's not already set or if the current active section doesn't exist in the new sections
            if (!activeSection || !sections.some(section => section.id === activeSection)) {
                setActiveSection(sections[0].id);
            }
        }
    }, [sections, activeSection]);

    // Handle verification data when it changes
    useEffect(() => {
        if (verificationCategories) {
            console.log('Verifications fetched:', verificationCategories);
            mapVerificationsToSections(verificationCategories as VerificationCategoryType[]);
        }
    }, [verificationCategories]);

    // Handle verification errors
    useEffect(() => {
        if (verificationsError) {
            console.error('Error fetching verifications:', verificationsError);
            toast.error(`Failed to load verifications: ${(verificationsError as Error).message || 'Unknown error'}`);
        }
    }, [verificationsError]);

    // Log whenever loanApplicationId changes
    useEffect(() => {
        console.log('loanApplicationId changed:', loanApplicationId);
    }, [loanApplicationId]);

    // Map API verification data to our component state
    const mapVerificationsToSections = (categories: VerificationCategoryType[]) => {
        console.log('Mapping verification categories to sections:', categories);

        if (!categories || categories.length === 0) {
            console.warn('No verification categories provided to map');
            return [];
        }

        // Debug: Log all item IDs from the API to see their format
        categories.forEach(category => {
            console.log(`Category ${category.categoryCode} items:`,
                (category.items || []).map(item => ({ id: item.id, label: item.label }))
            );
        });

        const mappedSections: VerificationSection[] = categories
            .filter(category => {
                // Filter out collateral section if loan is unsecured
                if (category.categoryCode?.toLowerCase() === 'collateral' &&
                    loanApplication?.loanDetails?.securityType === 'UNSECURED') {
                    console.log('Filtering out collateral section for unsecured loan');
                    return false;
                }
                // Filter out categories with totalCount of 0
                if (category.totalCount === 0) {
                    console.log(`Filtering out ${category.categoryCode} section with totalCount of 0`);
                    return false;
                }
                return true;
            })
            .map(category => {
                // Map category code to icon
                const getIconForCategory = (code: string | undefined) => {
                    switch (code?.toLowerCase()) {
                        case 'identity': return 'fa-id-card';
                        case 'bank': return 'fa-university';
                        case 'business': return 'fa-file-contract';
                        case 'collateral': return 'fa-shield-alt';
                        case 'company': return 'fa-building';
                        case 'screening': return 'fa-search';
                        default: return 'fa-check-circle';
                    }
                };

                // Create a map of items by their check code for easy lookup
                const itemsMap = new Map(
                    (category.items || []).map(item => {
                        const checkCode = getCheckCode(item);
                        return [checkCode, {
                            id: checkCode,
                            label: item.label || '',
                            status: (item.status || 'PENDING') as 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NOT_PROVIDED',
                            required: item.required || false,
                            requiresUpload: item.requiresUpload || false,
                            notes: item.notes,
                            verifiedByName: item.verifiedByName,
                            verifiedAt: item.verifiedAt
                        }];
                    })
                );

                // Get the predefined order for this category
                const categoryCode = category.categoryCode?.toLowerCase() || '';
                const orderedItemIds = ITEM_ORDER[categoryCode] || [];

                // Known checks in their set order, then any the order does not name. A check left off
                // the screen could never be decided, yet the backend still requires it -- which is how
                // "Photo Matching" silently blocked every submit.
                const mappedSection = {
                    id: category.categoryCode || '',
                    title: category.title || '',
                    icon: getIconForCategory(category.categoryCode),
                    items: [
                        ...orderedItemIds
                            .map(itemId => itemsMap.get(itemId))
                            .filter(Boolean) as VerificationItem[],
                        ...Array.from(itemsMap.values()).filter(item => !orderedItemIds.includes(item.id))
                    ]
                };

                console.log(`Mapped section ${mappedSection.id}:`, mappedSection);
                return mappedSection;
            });

        console.log('All mapped sections:', mappedSections);

        if (mappedSections.length > 0) {
            console.log('Setting sections state with:', mappedSections);
            setSections(mappedSections);
        } else {
            console.warn('No verification sections found after mapping');
        }

        return mappedSections;
    };

    // Update loading state based on API calls
    useEffect(() => {
        const isTaskLoading = !!taskId && isLoadingApplication;
        const isVerificationsLoading = !!loanApplicationId && isLoadingVerifications;
        const isDocumentsLoading = !!loanApplicationId && isLoadingDocuments;

        setIsLoading(isTaskLoading || isVerificationsLoading || isDocumentsLoading);

        console.log('Loading state:', {
            isTaskLoading,
            isVerificationsLoading,
            isDocumentsLoading,
            taskId,
            loanApplicationId,
            isLoadingApplication,
            isLoadingVerifications,
            isLoadingDocuments
        });
    }, [isLoadingApplication, isLoadingVerifications, isLoadingDocuments, taskId, loanApplicationId]);

    // Manually trigger verification fetch if needed
    const triggerVerificationFetch = () => {
        if (loanApplicationId) {
            console.log('Manually triggering verification fetch for application:', loanApplicationId);
            refetchVerifications();
        } else {
            console.error('Cannot fetch verifications: No loan application ID available');
            toast.error('Cannot fetch verifications without an application ID');
        }
    };

    const handleCancelApplication = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        const appId = loanApplicationId;
        if (!appId) {
            toast.error('No application ID available');
            return;
        }
        setIsCancelling(true);
        try {
            await cancelLoanApplication(appId, { reason: cancelReason.trim() });
            toast.success('Application cancelled successfully');
            setShowCancelModal(false);
            setCancelReason('');
            navigate('/applications');
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
            toast.error(message);
        } finally {
            setIsCancelling(false);
        }
    };

    // Mutation for updating verification categories (for bulk section actions)
    const updateVerificationCategoryMutation = useUpdateVerificationCategory({
        mutation: {
            onSuccess: async () => {
                toast.success('Section updated successfully');
                // Refetch verifications to get the latest data
                if (loanApplicationId) {
                    await refetchVerifications();
                }
            },
            onError: (error: any) => {
                console.error('Error updating verification section:', error);
                toast.error(`Failed to update section: ${error?.message || 'Unknown error'}`);
            }
        }
    });

    // Mutation for updating individual verification items
    const updateVerificationItemMutation = useUpdateVerificationItem({
        mutation: {
            onSuccess: async () => {
                toast.success('Verification item updated successfully');
                // Refetch verifications to get the latest data
                if (loanApplicationId) {
                    await refetchVerifications();
                }
            },
            onError: (error: any) => {
                console.error('Error updating verification item:', error);
                toast.error(`Failed to update verification item: ${error?.message || 'Unknown error'}`);
            }
        }
    });

    // Upload document mutation
    const uploadDocumentMutation = useUploadDocument();

    // Handle file upload for verification items
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, itemId: string) => {
        if (!e.target.files || e.target.files.length === 0) return;

        try {
            setIsUploading(true);
            setUploadError(null);
            const file = e.target.files[0];

            // File size validation (100MB limit)
            if (file.size > 100 * 1024 * 1024) {
                toast.error(`File size exceeds 100MB limit: ${file.name}`);
                return;
            }

            // File type validation
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`);
                return;
            }

            const documentType = CHECK_DOCUMENT_TYPES[itemId]?.[0];
            if (!documentType) {
                toast.error('Unable to determine document type for this verification item');
                return;
            }

            // Generate unique filename
            const timestamp = new Date().getTime().toString().slice(-4);
            const fileExtension = file.name.split('.').pop();
            const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
            const uniqueFileName = `${baseName}_${timestamp}.${fileExtension}`;

            // Create new File object with unique name
            const uniqueFile = new File([file], uniqueFileName, { type: file.type });

            if (!loanApplicationId) {
                toast.error('Cannot upload: No loan application ID available');
                return;
            }

            // Upload the document
            await uploadDocumentMutation.mutateAsync({
                loanApplicationId: loanApplicationId,
                data: {
                    file: uniqueFile,
                    documentType: documentType as UploadDocumentBodyDocumentType,
                    description: `Verification document for ${itemId.toLowerCase().replace(/_/g, ' ')}`
                }
            });

            // Refetch documents to get the latest data
            await refetchDocuments();

            toast.success('Document uploaded successfully');

        } catch (error) {
            console.error('Error uploading file:', error);

            let errorMessage = 'An error occurred while uploading the file';
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as any;
                if (axiosError.response?.status === 413) {
                    errorMessage = 'File is too large. Server rejected the upload.';
                } else if (axiosError.response?.status === 408 || axiosError.code === 'ECONNABORTED') {
                    errorMessage = 'Upload timeout. Please try again with a smaller file or check your connection.';
                } else if (axiosError.response?.data?.message) {
                    errorMessage = axiosError.response.data.message;
                } else if (axiosError.message) {
                    errorMessage = axiosError.message;
                }
            }

            setUploadError(errorMessage);
            toast.error('Failed to upload file: ' + errorMessage);
        } finally {
            setIsUploading(false);
            if (e.target) {
                e.target.value = '';
            }
        }
    };

    // Handle document download
    const handleDownloadDocument = async (documentId: string, documentName: string) => {
        try {
            setDownloadingDocuments(prev => new Set(prev).add(documentId));

            const blob = await downloadDocument(documentId);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Document downloaded successfully');
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download document');
        } finally {
            setDownloadingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });
        }
    };

    const handleVerificationUpdate = (
        sectionId: string,
        itemId: string,
        status: VerificationItem['status'],
        reason?: string
    ) => {
        const notes = reason || `Item marked as ${status.toLowerCase()}`;

        // Update local state first for immediate UI feedback
        setSections(currentSections =>
            currentSections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        items: section.items.map(item =>
                            item.id === itemId ? { ...item, status, notes, verifiedByName: recordedBy } : item
                        )
                    };
                }
                return section;
            })
        );

        // Only proceed with API call if we have a valid loan application ID
        if (!loanApplicationId) {
            toast.warning('Cannot update verification: No loan application ID available');
            return;
        }

        // Call the API to update the verification item
        const verificationUpdate: VerificationUpdate = {
            status: status as VerificationStatus,
            notes,
            recordedBy
        };

        // The itemCode should be the item ID itself (which is already in uppercase format like BANK_STATEMENTS)
        // Not a UUID or other identifier
        const itemCode = itemId;

        console.log('Updating verification item with API:', {
            applicationId: loanApplicationId,
            categoryCode: sectionId,
            itemCode: itemCode,
            data: verificationUpdate
        });

        updateVerificationItemMutation.mutate({
            applicationId: loanApplicationId,
            categoryCode: sectionId,
            itemCode: itemCode,
            data: verificationUpdate
        });
    };

    const handleConfirmItemRejection = () => {
        if (!itemToReject || !itemRejectionReason.trim()) return;
        handleVerificationUpdate(itemToReject.sectionId, itemToReject.item.id, 'REJECTED', itemRejectionReason.trim());
        setItemToReject(null);
        setItemRejectionReason('');
    };

    const handleSectionAction = (sectionId: string, action: 'complete' | 'reject') => {
        // Update local state first for immediate UI feedback
        setSections(currentSections =>
            currentSections.map(section => {
                if (section.id === sectionId) {
                    return {
                        ...section,
                        items: section.items.map(item => ({
                            ...item,
                            status: action === 'complete' ? 'VERIFIED' : 'REJECTED'
                        }))
                    };
                }
                return section;
            })
        );

        // Only proceed with API call if we have a valid loan application ID
        if (!loanApplicationId) {
            toast.warning('Cannot update verification: No loan application ID available');
            return;
        }

        // Call the API to update the verification category
        const verificationUpdate: VerificationUpdate = {
            status: action === 'complete' ? VerificationStatus.VERIFIED : VerificationStatus.REJECTED,
            notes: `All items ${action === 'complete' ? 'verified' : 'rejected'} in bulk`,
            recordedBy
        };

        console.log('Updating verification category with API:', {
            applicationId: loanApplicationId,
            categoryCode: sectionId,
            data: verificationUpdate
        });

        updateVerificationCategoryMutation.mutate({
            applicationId: loanApplicationId,
            categoryCode: sectionId,
            data: verificationUpdate
        });
    };

    const getSectionStatus = (section: VerificationSection) => {
        if (section.items.some(item => item.status === 'REJECTED')) return 'rejected';
        if (section.items.every(item => item.status === 'VERIFIED')) return 'verified';
        if (section.items.some(item => item.status === 'VERIFIED')) return 'partial';
        return 'pending';
    };

    /**
     * Every required item has an outcome recorded -- verified or rejected.
     *
     * Rejecting a single document used to make the verification unsubmittable, which left the
     * officer with only the all-or-nothing "Reject verification" action and no way to say "the
     * entity checks out, this one document does not". A rejection is an outcome, not an absence
     * of one, so it counts as decided; the rejected items travel with the verification for credit
     * to weigh. Rejecting the application outright remains its own deliberate action.
     */
    const areAllRequiredItemsVerified = () => {
        let allDecided = true;
        let hasRequiredItems = false;

        sections.forEach(section => {
            section.items.forEach(item => {
                if (item.required) {
                    hasRequiredItems = true;
                    if (item.status !== 'VERIFIED' && item.status !== 'REJECTED') {
                        allDecided = false;
                    }
                }
            });
        });

        return hasRequiredItems && allDecided;
    };

    /** Required items the officer has rejected; named on the submit confirmation. */
    const rejectedRequiredItems = sections.flatMap(section =>
        section.items.filter(item => item.required && item.status === 'REJECTED')
    );

    // Count total number of required items
    const countTotalRequiredItems = () => {
        let count = 0;
        sections.forEach(section => {
            section.items.forEach(item => {
                if (item.required) {
                    count++;
                }
            });
        });
        return count;
    };

    // Count number of verified required items
    const countVerifiedRequiredItems = () => {
        let count = 0;
        sections.forEach(section => {
            section.items.forEach(item => {
                if (item.required && item.status === 'VERIFIED') {
                    count++;
                }
            });
        });
        return count;
    };

    // Progress is how many required items have an outcome, so it reaches 100% exactly when submit
    // unlocks -- a rejection moves it forward just as a verification does.
    const calculateVerificationProgress = () => {
        const total = countTotalRequiredItems();
        if (total === 0) return 0;

        const decided = countVerifiedRequiredItems() + rejectedRequiredItems.length;
        return Math.round((decided / total) * 100);
    };

    // Custom mutations for submit and reject verification
    const submitVerificationMutation = useMutation({
        mutationFn: async (applicationId: string) => {
            return await customAxiosInstanceMutator({
                url: `/loan/api/v1/applications/${applicationId}/verifications/submit`,
                method: 'POST',
            });
        },
        onSuccess: () => {
            toast.success('Verification submitted successfully');
            setIsSubmitted(true);
        },
        onError: (error: any) => {
            toast.error(`Failed to submit verification: ${error.response?.data?.message || 'Unknown error'}`);
        }
    });

    const rejectVerificationMutation = useMutation({
        mutationFn: async ({ applicationId, comment, declineReason, rejectedBy }:
                           { applicationId: string; comment: string; declineReason: string; rejectedBy?: string }) => {
            return await customAxiosInstanceMutator({
                url: `/loan/api/v1/applications/${applicationId}/verifications/reject`,
                method: 'POST',
                data: { comment, declineReason, rejectedBy }
            });
        },
        onSuccess: (data) => {
            toast.success('Verification rejected successfully');
            setIsRejected(true);
            setShowRejectForm(false);
            setRejectionComment('');
            setRejectionReason('');
        },
        onError: (error: any) => {
            toast.error(`Failed to reject verification: ${error.response?.data?.message || 'Unknown error'}`);
        }
    });

    // Handle submission of verification
    const handleSubmitVerification = () => {
        const verificationUpdatePending =
            updateVerificationItemMutation.status === 'pending' ||
            updateVerificationCategoryMutation.status === 'pending';

        if (verificationUpdatePending) {
            toast.info('Please wait for the verification update to finish before submitting.');
            return;
        }

        if (!areAllRequiredItemsVerified()) {
            toast.error('Cannot submit: every required item needs an outcome recorded first');
            return;
        }

        if (!loanApplicationId) {
            toast.error('Cannot submit: No loan application ID available');
            return;
        }

        // Passing a rejection on to credit is a deliberate act, so it is acknowledged rather than
        // slipping through with the rest of the outcomes.
        if (rejectedRequiredItems.length > 0) {
            setShowSubmitWithRejectionsModal(true);
            return;
        }

        submitVerificationMutation.mutate(loanApplicationId);
    };

    const handleConfirmSubmitWithRejections = () => {
        setShowSubmitWithRejectionsModal(false);
        if (loanApplicationId) submitVerificationMutation.mutate(loanApplicationId);
    };

    // Handle showing the reject form
    const handleShowRejectForm = () => {
        setShowRejectForm(true);
    };

    // Handle canceling rejection
    const handleCancelReject = () => {
        setShowRejectForm(false);
        setRejectionComment('');
    };

    // Handle rejection of verification
    const handleRejectVerification = () => {
        if (!loanApplicationId) {
            toast.error('Cannot reject: No loan application ID available');
            return;
        }

        if (!rejectionComment.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        if (!rejectionReason) {
            toast.error('Choose a reason for rejection');
            return;
        }

        // Call the API to reject the verification
        rejectVerificationMutation.mutate({
            applicationId: loanApplicationId,
            comment: rejectionComment,
            declineReason: rejectionReason,
            rejectedBy: user?.name || user?.username
        });
    };

    // Debug sections state
    console.log('Sections state:', sections);
    console.log('Loan Application:', loanApplication || 'Not loaded');
    console.log('Verification Categories:', verificationCategories || 'Not loaded');

    // Calculate verification stats
    const verificationStats = {
        total: verificationCategories?.reduce((acc, cat) => acc + (cat.totalCount || 0), 0) || 0,
        verified: verificationCategories?.reduce((acc, cat) => acc + (cat.verifiedCount || 0), 0) || 0
    };

    // Map verification categories to sections if available
    useEffect(() => {
        if (verificationCategories && verificationCategories.length > 0) {
            console.log('Mapping verification categories to sections:', verificationCategories);
            const mappedSections = mapVerificationsToSections(verificationCategories);
            setSections(mappedSections);
        }
    }, [verificationCategories]);

    // Display loading state
    if (isLoading) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading verification details...</p>
                            <small className="text-muted">
                                {isLoadingApplication ? 'Fetching application from task...' : 'Loading verifications...'}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Display error state
    if (applicationError || verificationsError) {
        const errorMessage = applicationError
            ? (applicationError as Error).message || 'Unknown application error'
            : verificationsError
                ? (verificationsError as Error).message || 'Unknown verification error'
                : 'An unknown error occurred';

        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="alert alert-danger">
                                <h5 className="alert-heading">Error Loading Data</h5>
                                <p>{errorMessage}</p>
                                <div className="d-flex gap-2 mt-3">
                                    <button className="btn btn-outline-danger" onClick={() => refetchVerifications()}>
                                        <i className="fa fa-refresh me-2"></i>
                                        Retry Verifications
                                    </button>
                                    {loanApplicationId && (
                                        <button className="btn btn-outline-primary" onClick={triggerVerificationFetch}>
                                            <i className="fa fa-sync me-2"></i>
                                            Force Fetch Verifications
                                        </button>
                                    )}
                                </div>
                                <div className="mt-3">
                                    <small className="text-muted">
                                        Task ID: {taskId}<br />
                                        Application ID: {loanApplicationId || 'Not available'}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // If we have a loan application ID but no verification data, show a message
    if (loanApplicationId && (!verificationCategories || verificationCategories.length === 0) && !isLoadingVerifications) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="alert alert-warning">
                                <h5 className="alert-heading">No Verification Data</h5>
                                <p>We found the application, but no verification items are available.</p>
                                <button className="btn btn-primary" onClick={triggerVerificationFetch}>
                                    <i className="fa fa-refresh me-2"></i>
                                    Fetch Verification Data
                                </button>
                                <div className="mt-3">
                                    <small className="text-muted">
                                        Task ID: {taskId}<br />
                                        Application ID: {loanApplicationId}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Display rejection success state
    if (isRejected) {
        return (
            <div className="container-fluid p-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mb-3">Verification Rejected Successfully</h2>
                        <p className="text-muted mb-4">The loan application verification has been rejected.</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate('/work-items')}
                        >
                            Return to Work Items
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Display submission success state
    if (isSubmitted) {
        return (
            <div className="container-fluid p-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                        </div>
                        <h2 className="mb-3">Verification Submitted Successfully</h2>
                        <p className="text-muted mb-4">The loan application verification has been submitted successfully.</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => navigate('/work-items')}
                        >
                            Return to Work Items
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Debug sections state
    console.log('Current sections state:', sections);
    console.log('Current active section:', activeSection);

    return (
        <>
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/work-items">Work Items</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    Application {loanApplication?.applicationNumber || loanApplicationId || taskId}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h6 className="card-title mb-0">
                                        Application Verification - {loanApplication?.applicationNumber || loanApplicationId || taskId}
                                        {loanApplication?.customerNumber && (
                                            <span className="ms-2 text-muted">
                                                | Customer: {loanApplication.customerNumber}
                                            </span>
                                        )}
                                    </h6>
                                    <div className="d-flex gap-2">
                                        <Link
                                            to={`/application/${loanApplication?.id}`}
                                            state={{ fromVerification: true, taskId }}
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            <i className="bi bi-eye me-2"></i>
                                            View Application
                                        </Link>
                                        
                                        <button
                                            className="btn btn-sm btn-primary"
                                            onClick={triggerVerificationFetch}
                                        >
                                            <i className="fa fa-refresh me-1"></i> Refresh
                                        </button>

                                        {!showRejectForm && (
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={handleShowRejectForm}
                                                disabled={rejectVerificationMutation.status === 'pending'}
                                            >
                                                <i className="fa fa-times-circle me-1"></i> Reject Verification
                                            </button>
                                        )}

                                        <button
                                            className="btn btn-sm btn-outline-info"
                                            onClick={() => setShowCommentsModal(true)}
                                        >
                                            <i className="fa fa-comment me-1"></i> Add Comment
                                        </button>

                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setShowCancelModal(true)}
                                        >
                                            <i className="fa fa-times-circle me-1"></i> Cancel Application
                                        </button>

                                        <button
                                            className={`btn btn-sm ${areAllRequiredItemsVerified() ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={handleSubmitVerification}
                                            disabled={!areAllRequiredItemsVerified() || submitVerificationMutation.status === 'pending' || updateVerificationItemMutation.status === 'pending' || updateVerificationCategoryMutation.status === 'pending' || showRejectForm}
                                            title={!areAllRequiredItemsVerified() ? "Every required item needs to be verified or rejected" : "Submit verification"}
                                        >
                                            {submitVerificationMutation.status === 'pending' ? (
                                                <>
                                                    <i className="fa fa-spinner fa-spin me-1"></i> Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className={`fa ${areAllRequiredItemsVerified() ? 'fa-check-square-o' : 'fa-clock-o'} me-1`}></i>
                                                    Submit Verification
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body">
                                    {/* Rejection Form */}
                                    {showRejectForm && (
                                        <div className="alert alert-danger mb-4">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-grow-1">
                                                    <h6 className="alert-heading mb-3">
                                                        <i className="fa fa-exclamation-triangle me-2"></i>
                                                        Reject Verification
                                                    </h6>
                                                    <p className="mb-3">Please provide a reason for rejecting this verification:</p>
                                                    <div className="mb-3">
                                                        <select
                                                            className="form-select"
                                                            value={rejectionReason}
                                                            onChange={(e) => setRejectionReason(e.target.value)}
                                                            disabled={rejectVerificationMutation.status === 'pending'}
                                                            aria-label="Reason for rejection"
                                                        >
                                                            <option value="">Select a reason</option>
                                                            {Object.entries(DECLINE_REASON_LABELS).map(([value, label]) => (
                                                                <option key={value} value={value}>{label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="mb-3">
                                                        <textarea
                                                            className="form-control"
                                                            rows={4}
                                                            placeholder="Enter reason for rejection..."
                                                            value={rejectionComment}
                                                            onChange={(e) => setRejectionComment(e.target.value)}
                                                            disabled={rejectVerificationMutation.status === 'pending'}
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-danger"
                                                            onClick={handleRejectVerification}
                                                            disabled={rejectVerificationMutation.status === 'pending' || !rejectionComment.trim() || !rejectionReason}
                                                        >
                                                            {rejectVerificationMutation.status === 'pending' ? (
                                                                <>
                                                                    <i className="fa fa-spinner fa-spin me-2"></i>
                                                                    Rejecting...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fa fa-times-circle me-2"></i>
                                                                    Confirm Rejection
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            onClick={handleCancelReject}
                                                            disabled={rejectVerificationMutation.status === 'pending'}
                                                        >
                                                            <i className="fa fa-times me-2"></i>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="row g-3">
                                        {/* Navigation Sidebar */}
                                        <div className="col-12 col-lg-3 mb-3">
                                            <div className="list-group">
                                                {sections.map((section) => {
                                                    const status = getSectionStatus(section);
                                                    return (
                                                        <button
                                                            key={section.id}
                                                            className={`list-group-item list-group-item-action d-flex align-items-center ${activeSection === section.id ? 'active bg-success text-white' : ''}`}
                                                            onClick={() => setActiveSection(section.id)}
                                                        >
                                                            <i className={`fa ${section.icon} me-3`}></i>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span>{section.title}</span>
                                                                    <i className={`fa fa-circle fs-8 ms-2 ${
                                                                        status === 'verified' ? 'text-success' :
                                                                        status === 'rejected' ? 'text-danger' :
                                                                        status === 'partial' ? 'text-warning' : 'text-muted'
                                                                    }`}></i>
                                                                </div>
                                                                <small className={activeSection === section.id ? 'text-white-50' : 'text-muted'}>
                                                                    {section.items.filter(i => i.status === 'VERIFIED').length} of {section.items.length} verified
                                                                </small>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Verification Content */}
                                        <div className="col-12 col-lg-9">
                                            {sections.length === 0 ? (
                                                <div className="alert alert-info">
                                                    <h5 className="alert-heading">No Verification Sections</h5>
                                                    <p>No verification sections were found for this application.</p>
                                                    <p className="mb-0">
                                                        <strong>Debug Info:</strong><br/>
                                                        Verification Categories: {verificationCategories ? `${verificationCategories.length} categories` : 'None'}<br/>
                                                        Loading: {isLoadingVerifications ? 'Yes' : 'No'}<br/>
                                                        Application ID: {loanApplicationId || 'None'}
                                                    </p>
                                                    {verificationCategories && verificationCategories.length > 0 && (
                                                        <div className="mt-3">
                                                            <h6>Raw Verification Data:</h6>
                                                            <pre className="bg-light p-3" style={{ maxHeight: '300px', overflow: 'auto' }}>
                                                                {JSON.stringify(verificationCategories, null, 2)}
                                                            </pre>
                                                            <button
                                                                className="btn btn-primary mt-2"
                                                                onClick={() => {
                                                                    console.log('Manually mapping verification data');
                                                                    mapVerificationsToSections(verificationCategories);
                                                                }}
                                                            >
                                                                <i className="fa fa-code me-2"></i>
                                                                Map Verification Data
                                                            </button>
                                                        </div>
                                                    )}
                                                    <button className="btn btn-primary mt-3" onClick={triggerVerificationFetch}>
                                                        <i className="fa fa-refresh me-2"></i>
                                                        Refresh Data
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Upload Error Display */}
                                                    {uploadError && (
                                                        <div className="alert alert-danger mb-4">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <span><i className="fa fa-exclamation-triangle me-2"></i>{uploadError}</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn-close"
                                                                    onClick={() => setUploadError(null)}
                                                                    aria-label="Close"
                                                                ></button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Verification Progress Information */}
                                                    <div className="alert alert-info mb-4">
                                                        <div className="d-flex">
                                                            <div className="me-3">
                                                                <i className="fa fa-info-circle fa-2x"></i>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <h6 className="alert-heading mb-1">Verification Progress</h6>
                                                                <p className="mb-0">
                                                                    {!areAllRequiredItemsVerified()
                                                                        ? 'Verify or reject every required item (marked with *) before submitting.'
                                                                        : rejectedRequiredItems.length > 0
                                                                            ? `Every required item has an outcome. ${rejectedRequiredItems.length} rejected ${rejectedRequiredItems.length === 1 ? 'item goes' : 'items go'} to credit with the submission.`
                                                                            : 'All required items have been verified. You can now submit the verification.'}
                                                                </p>
                                                                <div className="progress mt-2" style={{ height: '8px' }}>
                                                                    <div
                                                                        className="progress-bar bg-success"
                                                                        role="progressbar"
                                                                        style={{
                                                                            width: `${calculateVerificationProgress()}%`
                                                                        }}
                                                                        aria-valuenow={calculateVerificationProgress()}
                                                                        aria-valuemin={0}
                                                                        aria-valuemax={100}
                                                                    ></div>
                                                                </div>
                                                                <div className="d-flex justify-content-between mt-1">
                                                                    <small>Progress: {calculateVerificationProgress()}%</small>
                                                                    <small>
                                                                        {countVerifiedRequiredItems()} verified
                                                                        {rejectedRequiredItems.length > 0 && `, ${rejectedRequiredItems.length} rejected`}
                                                                        {' '}of {countTotalRequiredItems()} required
                                                                    </small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {sections.map((section) => (
                                                        <div key={section.id} className={activeSection === section.id ? '' : 'd-none'}>
                                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                                <h5 className="mb-0">
                                                                    <i className={`fa ${section.icon} me-2`}></i>
                                                                    {section.title}
                                                                </h5>
                                                                <div className="badge bg-light text-dark">
                                                                    {section.items.filter(i => i.status === 'VERIFIED').length} of {section.items.length} completed
                                                                    {section.items.some(i => i.required) && (
                                                                        <span className="ms-2">
                                                                            ({section.items.filter(i => i.required && i.status === 'VERIFIED').length} of {section.items.filter(i => i.required).length} required)
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {section.items.map((item) => {
                                                                const itemDocuments = getCheckDocuments(item.id, documents);
                                                                const expectsDocument = !!CHECK_DOCUMENT_TYPES[item.id];

                                                                return (
                                                                <div key={item.id} className="card mb-3">
                                                                    <div className="card-body">
                                                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                                                <div className="flex-grow-1">
                                                                                <h6 className="mb-1">
                                                                                    {item.label}
                                                                                    {item.required && (
                                                                                        <span className="text-danger ms-1 fw-bold">*</span>
                                                                                    )}
                                                                                </h6>
                                                                                <span className={`badge ${
                                                                                    item.status === 'VERIFIED' ? 'bg-success' :
                                                                                    item.status === 'REJECTED' ? 'bg-danger' :
                                                                                    item.status === 'NOT_PROVIDED' ? 'bg-warning' : 'bg-info'
                                                                                }`}>
                                                                                    {item.status.split('_').map(word =>
                                                                                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                                                                    ).join(' ')}
                                                                                </span>
                                                                                {item.status === 'REJECTED' && item.notes && (
                                                                                    <div className="small text-danger mt-1">
                                                                                        <i className="fa fa-comment-o me-1"></i>
                                                                                        {item.notes}
                                                                                    </div>
                                                                                )}
                                                                                {item.status !== 'PENDING' && item.verifiedByName && (
                                                                                    <div className="small text-muted mt-1">
                                                                                        <i className="fa fa-user-o me-1"></i>
                                                                                        {item.verifiedByName}
                                                                                        {item.verifiedAt && ` · ${formatDateTime(item.verifiedAt)}`}
                                                                                    </div>
                                                                                )}

                                                                                    {/* The documents this check is ruled on, each readable in place. */}
                                                                                    {itemDocuments.length > 0 && (
                                                                                        <div className="mt-2 d-flex flex-column gap-2">
                                                                                            {itemDocuments.map(doc => {
                                                                                                const documentKey = doc.documentKey || doc.id || '';
                                                                                                return (
                                                                                                    <div key={doc.id || documentKey} className="d-flex align-items-center gap-2 flex-wrap">
                                                                                                        <small className="text-success">
                                                                                                            <i className="fa fa-file me-1"></i>
                                                                                                            {doc.name || 'Unnamed document'}
                                                                                                        </small>
                                                                                                        <small className="text-muted">
                                                                                                            {String(doc.documentType || '').replace(/_/g, ' ').toLowerCase()}
                                                                                                            {doc.directorName && ` · ${doc.directorName}`}
                                                                                                        </small>
                                                                                                        {/* Verification is where a stale document matters most, so flag it here too. */}
                                                                                                        <DocumentValidityBadge uploadDate={getDocumentUploadDate(doc)} status={doc.validityStatus} validUntil={doc.validUntil} rule={doc.validityRule} />
                                                                                                        {/* An officer has to read a document before ruling on it, so view comes before download */}
                                                                                                        <DocumentActionsMenu
                                                                                                            id={`${item.id}-${doc.id || documentKey}`}
                                                                                                            actions={[
                                                                                                                {
                                                                                                                    label: 'View',
                                                                                                                    icon: 'fa-eye',
                                                                                                                    busy: documentPreview.openingKey === documentKey,
                                                                                                                    onClick: () => documentPreview.open(documentKey, doc.name)
                                                                                                                },
                                                                                                                {
                                                                                                                    label: 'Download',
                                                                                                                    icon: 'fa-download',
                                                                                                                    busy: downloadingDocuments.has(documentKey),
                                                                                                                    onClick: () => handleDownloadDocument(documentKey, doc.name || 'document')
                                                                                                                }
                                                                                                            ]}
                                                                                                        />
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                    {expectsDocument && itemDocuments.length === 0 && (
                                                                                        <div className="mt-2">
                                                                                            <small className="text-muted">
                                                                                                <i className="fa fa-file-o me-1"></i>
                                                                                                No document on file for this check
                                                                                            </small>
                                                                                        </div>
                                                                                    )}
                                                                            </div>

                                                                                <div className="d-flex gap-2 align-items-start">
                                                                                    {/* Upload controls for items that require uploads */}
                                                                                    {item.requiresUpload && (
                                                                                        <div>
                                                                                            <input
                                                                                                type="file"
                                                                                                id={`file-upload-${item.id}`}
                                                                                                className="d-none"
                                                                                                accept=".pdf,.jpg,.jpeg,.png"
                                                                                                onChange={(e) => handleFileUpload(e, item.id)}
                                                                                                disabled={isUploading}
                                                                                            />
                                                                                            <label
                                                                                                htmlFor={`file-upload-${item.id}`}
                                                                                                className={`btn btn-sm ${itemDocuments.length > 0 ? 'btn-outline-primary' : 'btn-primary'}`}
                                                                                                title={itemDocuments.length > 0 ? "Upload a newer document" : "Upload document"}
                                                                                            >
                                                                                                {isUploading ? (
                                                                                                    <i className="fa fa-spinner fa-spin"></i>
                                                                                                ) : (
                                                                                                    <i className={`fa ${itemDocuments.length > 0 ? 'fa-refresh' : 'fa-upload'}`}></i>
                                                                                                )}
                                                                                            </label>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Verification action buttons */}
                                                                                    <div className="d-flex gap-1">
                                                                                <button
                                                                                    className={`btn btn-sm ${item.status === 'VERIFIED' ? 'btn-success' : 'btn-outline-success'}`}
                                                                                    onClick={() => handleVerificationUpdate(section.id, item.id, 'VERIFIED')}
                                                                                    data-tooltip-id={`verify-${item.id}`}
                                                                                    disabled={updateVerificationItemMutation.status === 'pending'}
                                                                                >
                                                                                    <i className={`fa ${updateVerificationItemMutation.status === 'pending' ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                                                                                </button>
                                                                                <Tooltip id={`verify-${item.id}`} place="top" content="Verify item" />

                                                                                <button
                                                                                    className={`btn btn-sm ${item.status === 'REJECTED' ? 'btn-danger' : 'btn-outline-danger'}`}
                                                                                    onClick={() => {
                                                                                        setItemRejectionReason(item.status === 'REJECTED' ? item.notes || '' : '');
                                                                                        setItemToReject({ sectionId: section.id, item });
                                                                                    }}
                                                                                    data-tooltip-id={`reject-${item.id}`}
                                                                                    disabled={updateVerificationItemMutation.status === 'pending'}
                                                                                >
                                                                                    <i className={`fa ${updateVerificationItemMutation.status === 'pending' ? 'fa-spinner fa-spin' : 'fa-times'}`}></i>
                                                                                </button>
                                                                                <Tooltip id={`reject-${item.id}`} place="top" content="Reject item" />

                                                                                <button
                                                                                    className={`btn btn-sm ${item.status === 'NOT_PROVIDED' ? 'btn-warning' : 'btn-outline-warning'}`}
                                                                                    onClick={() => handleVerificationUpdate(section.id, item.id, 'NOT_PROVIDED')}
                                                                                    data-tooltip-id={`not-provided-${item.id}`}
                                                                                    disabled={updateVerificationItemMutation.status === 'pending'}
                                                                                >
                                                                                    <i className={`fa ${updateVerificationItemMutation.status === 'pending' ? 'fa-spinner fa-spin' : 'fa-exclamation-circle'}`}></i>
                                                                                </button>
                                                                                <Tooltip id={`not-provided-${item.id}`} place="top" content="Mark as not provided" />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                    </div>
                                                                );
                                                            })}

                                                            <div className="mt-4 d-flex justify-content-between">
                                                                <button
                                                                    className="btn btn-success"
                                                                    onClick={() => handleSectionAction(section.id, 'complete')}
                                                                    disabled={updateVerificationCategoryMutation.status === 'pending'}
                                                                >
                                                                    <i className={`fa ${updateVerificationCategoryMutation.status === 'pending' ? 'fa-spinner fa-spin' : 'fa-check-circle'} me-2`}></i>
                                                                    Complete Section
                                                                </button>

                                                                <button
                                                                    className="btn btn-outline-danger"
                                                                    onClick={() => handleSectionAction(section.id, 'reject')}
                                                                    disabled={updateVerificationCategoryMutation.status === 'pending'}
                                                                >
                                                                    <i className={`fa ${updateVerificationCategoryMutation.status === 'pending' ? 'fa-spinner fa-spin' : 'fa-times-circle'} me-2`}></i>
                                                                    Reject Section
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    {/*
                                                      Every document on the application, not only the eight types that happen to map
                                                      to a checklist item. Anything else the customer supplied was unreachable from
                                                      this page, so an officer could not read what they were being asked to rule on.
                                                    */}
                                                    {documents && documents.length > 0 && (
                                                        <div className="card mb-3">
                                                            <div className="card-header d-flex justify-content-between align-items-center py-2">
                                                                <h6 className="mb-0">
                                                                    <i className="fa fa-folder-open me-2"></i>
                                                                    Application documents
                                                                </h6>
                                                                <span className="badge bg-secondary rounded-pill">{documents.length}</span>
                                                            </div>
                                                            <div className="list-group list-group-flush">
                                                                {documents.map(doc => (
                                                                    <div
                                                                        key={doc.id || doc.documentKey}
                                                                        className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2"
                                                                    >
                                                                        <div className="me-3">
                                                                            <div className="fw-semibold small">{doc.name || 'Unnamed document'}</div>
                                                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                                                <small className="text-muted">
                                                                                    {String(doc.documentType || '').replace(/_/g, ' ')}
                                                                                </small>
                                                                                <DocumentValidityBadge uploadDate={getDocumentUploadDate(doc)} status={doc.validityStatus} validUntil={doc.validUntil} rule={doc.validityRule} />
                                                                            </div>
                                                                        </div>
                                                                        <DocumentActionsMenu
                                                                            id={doc.id || doc.documentKey || ''}
                                                                            actions={[
                                                                                {
                                                                                    label: 'View',
                                                                                    icon: 'fa-eye',
                                                                                    busy: documentPreview.openingKey === (doc.documentKey || doc.id),
                                                                                    onClick: () => documentPreview.open(doc.documentKey || doc.id || '', doc.name)
                                                                                },
                                                                                {
                                                                                    label: 'Download',
                                                                                    icon: 'fa-download',
                                                                                    busy: downloadingDocuments.has(doc.documentKey || doc.id || ''),
                                                                                    onClick: () => handleDownloadDocument(doc.documentKey || doc.id || '', doc.name || 'document')
                                                                                }
                                                                            ]}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Cancel Application Modal */}
        {showCancelModal && (
            <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                        <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                    <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                                </div>
                                <div>
                                    <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                    <p className="text-muted mb-0 small">This action cannot be undone</p>
                                </div>
                            </div>
                            <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                        </div>
                        <div className="modal-body p-4">
                            <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Enter reason for cancellation..."
                                disabled={isCancelling}
                                maxLength={500}
                            />
                            <small className="text-muted">{cancelReason.length}/500 characters</small>
                        </div>
                        <div className="modal-footer border-0 px-4 pb-4">
                            <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger rounded-pill px-4"
                                onClick={handleCancelApplication}
                                disabled={isCancelling || !cancelReason.trim()}
                            >
                                {isCancelling ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                                ) : (
                                    <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Comments Modal */}
        <Modal show={showSubmitWithRejectionsModal} onHide={() => setShowSubmitWithRejectionsModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-6">
                    <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                    Submit with rejected items?
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">These required items are recorded as rejected:</p>
                <ul className="mb-3">
                    {rejectedRequiredItems.map(item => (
                        <li key={item.id}>
                            {item.label || item.id}
                            {item.notes && <span className="text-muted"> &mdash; {item.notes}</span>}
                        </li>
                    ))}
                </ul>
                <p className="text-muted small mb-0">
                    The verification is passed on with these rejections attached, for credit to weigh. To stop the
                    application outright, use Reject verification instead.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowSubmitWithRejectionsModal(false)}>
                    Go back
                </Button>
                <Button variant="warning" size="sm" onClick={handleConfirmSubmitWithRejections}>
                    Submit verification
                </Button>
            </Modal.Footer>
        </Modal>

        <Modal show={!!itemToReject} onHide={() => setItemToReject(null)} centered>
            <Modal.Header closeButton>
                <Modal.Title className="fs-6">
                    <i className="fa fa-times-circle text-danger me-2"></i>
                    Reject {itemToReject?.item.label}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <label htmlFor="item-rejection-reason" className="form-label small">
                    Why is this check rejected? Credit sees this with the verification.
                </label>
                <textarea
                    id="item-rejection-reason"
                    className="form-control"
                    rows={3}
                    maxLength={500}
                    value={itemRejectionReason}
                    onChange={e => setItemRejectionReason(e.target.value)}
                    placeholder="e.g. Registration certificate names a different company"
                    autoFocus
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" size="sm" onClick={() => setItemToReject(null)}>
                    Cancel
                </Button>
                <Button
                    variant="danger"
                    size="sm"
                    onClick={handleConfirmItemRejection}
                    disabled={!itemRejectionReason.trim()}
                >
                    Reject check
                </Button>
            </Modal.Footer>
        </Modal>

        <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />

        <Modal show={showCommentsModal} onHide={() => setShowCommentsModal(false)} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-comments me-2"></i>
                    Application Comments
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loanApplicationId && (
                    <LoanApplicationComments
                        loanApplicationId={loanApplicationId as string}
                        currentStage="VERIFICATION_IN_PROGRESS"
                        className="mt-2"
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowCommentsModal(false)}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default VerificationDetails;
